import './style.css';

const queryParams: Record<string, string> = {};
const searchParams = new URLSearchParams(window.location.search);
searchParams.forEach((value, key) => {
  queryParams[key] = value;
});

const pathname = window.location.pathname;
const lastSlashIndex = pathname.lastIndexOf('/');
const surveyId = pathname.substring(lastSlashIndex + 1);

const apiUrl = import.meta.env.VITE_LINKGEN_API_URL;

type GateErrorReason = 'gate_sdk_unavailable' | 'gate_session_failed' | 'gate_verify_failed';

let sessionId: string | undefined;

/**
 * Ends the respondent's visit. Closing the tab is the intended outcome, but browsers only honour
 * `window.close()` for windows that script itself opened, and panels normally navigate to the gate
 * rather than opening it - so the call is silently ignored more often than it works.
 *
 * The fallback matters: without it a turned-away respondent sits watching "verification in progress"
 * forever, which looks like the gate hanging rather than a decision. The wording stays neutral for
 * the same reason the API returns a bare 403 - the gate must not tell anyone why they were rejected.
 */
function endSession() {
  window.close();

  // Delayed so a close that does work never flashes the message on the way out.
  window.setTimeout(() => {
    const container = document.querySelector('.verification');
    if (!container) {
      return;
    }

    container.querySelector('.verification-spinner')?.remove();

    const title = container.querySelector('.verification-title');
    const message = container.querySelector('.verification-message');

    if (title) {
      title.textContent = 'You can close this window';
    }

    if (message) {
      message.textContent = 'This session has ended. Please return to the panel.';
    }
  }, 250);
}

/**
 * Tells the backend about failures that happen before or instead of `/verify`. Without this the
 * respondent's tab just closes and the failure exists nowhere but their console, so a link that is
 * broken for everyone looks like a link nobody clicked.
 *
 * Best-effort by design: it must never delay or replace the failure handling around it.
 */
async function reportError(reason: GateErrorReason, error?: unknown) {
  try {
    await fetch(`${apiUrl}/verify/report-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Survives the tab closing immediately afterwards, which a plain fetch does not.
      keepalive: true,
      body: JSON.stringify({
        reason,
        surveyId,
        sessionId: sessionId ?? undefined,
        rid: queryParams.RID,
        message: error instanceof Error ? error.message : String(error ?? '')
      })
    });
  } catch (reportingError) {
    console.log('Could not report gate error: ', reportingError);
  }
}

async function getSessionId() {
  if (!window.Verisoul) {
    console.log(
      'Verisoul not found. Please make sure you have included the Verisoul script in your index.html file.'
    );
    await reportError('gate_sdk_unavailable', 'window.Verisoul was never defined');
    return;
  }

  try {
    const { session_id } = await window.Verisoul.session();
    console.log('Session ID: ', session_id);

    if (!session_id) {
      await reportError('gate_session_failed', 'Verisoul.session() returned no session id');
      return;
    }

    return session_id;
  } catch (error) {
    console.log('Error getting session ID: ', error);
    await reportError('gate_session_failed', error);
  }
}

async function postSurveySession() {
  sessionId = await getSessionId();
  if (!sessionId) {
    // getSessionId has already reported why. Ending the session here too, so an SDK that never
    // loads leaves the same terminal screen as a rejection instead of an endless spinner.
    endSession();
    return;
  }

  try {
    const reqBody = JSON.stringify({
      queryParams,
      surveyId,
      sessionId
    });
    const reqUrl = `${apiUrl}/verify`;
    const verifyReq = await fetch(reqUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: reqBody
    });

    // A 403 is the backend deliberately turning this respondent away - a screenout, or a failure
    // it has already recorded itself. Reporting it back would double-count every one of them.
    if (verifyReq.status === 403) {
      endSession();
      return;
    }

    if (!verifyReq.ok) {
      throw new Error(`HTTP ${verifyReq.status}`);
    }

    const response = await verifyReq.json();
    if (!response.redirectUrl) {
      throw new Error('Response carried no redirect URL');
    }

    window.location.assign(response.redirectUrl);
  } catch (error) {
    console.log('Error posting survey session: ', error);
    await reportError('gate_verify_failed', error);
    endSession();
  }
}

postSurveySession();
